import LicenseTrialModel from '@shared/models/organizations/LicenseTrialModel';
import OrganizationMemberDetailsModel from '@shared/models/organizations/OrganizationMemberDetailsModel';

/**
 * Part 2 of Upgrade Flow: compare products, pick one, and redirect to appropriate link
 */

export type BillingLinkRequest =
    | { type: 'orgProductLink'; organizationId: string; productId: string } // expired org subscribing to a product, or a reseller buying license keys
    | { type: 'licensedOrgProductLink'; organizationId: string; productId: string } // modify existing subscription
    | { type: 'trialOrgProductLink'; organizationId: string; trialId: string; productId: string } // trial org converting to a paid product.
    | { type: 'managedOrgBillingLink'; organizationId: string; managedOrgId: string } // reseller managing a managed org subscription
    | { type: 'orgBillingLink'; organizationId: string }; // existing subscription

export type BillingRedirectDecision =
    | { type: 'permissionDenied' }
    | { type: 'navigateToCompare'; comparePath: string }
    | { type: 'openExternalBilling'; request: BillingLinkRequest; errorRedirectPath: string };

export interface IBillingLinkResolvers {
    getOrganizationProductLink: (organizationId: string, productId: string) => Promise<string | null>;
    getBillingLinkForOrgByProduct: (organizationId: string, productId: string) => Promise<string | null>;
    getTrialUpgradeLinkForProduct: (
        organizationId: string,
        trialId: string,
        productId: string
    ) => Promise<string | null>;
    getBillingLinkForManagedOrg: (organizationId: string, managedOrgId: string) => Promise<string | null>;
    getBillingLink: (organizationId: string) => Promise<string | null>;
}

interface IGetBillingRedirectDecisionProps {
    organizationId: string;
    organizationMembership: OrganizationMemberDetailsModel;
    managedOrgId?: string;
    productId?: string;
    now?: Date;
}

const assertUnreachable = (request: never): never => {
    throw new Error(`Unsupported billing link request: ${JSON.stringify(request)}`);
};

/** Most recently expiring trial first. */
const sortTrialsByExpiration = (trials?: LicenseTrialModel[]) =>
    [...(trials ?? [])].sort((a, b) => new Date(b.expirationDate).getTime() - new Date(a.expirationDate).getTime());

/** A Stripe-backed trial is a live subscription, so it is managed through the billing portal. */
const isStripeTrial = (trial?: LicenseTrialModel) => Boolean(trial?.isAutoUpdate && trial?.isCreatedFromStripe);

const hasBillingPermissions = ({
    organizationMembership,
    managedOrgId,
    productId,
}: Pick<IGetBillingRedirectDecisionProps, 'organizationMembership' | 'managedOrgId' | 'productId'>) =>
    Boolean(
        // Picking a specific product is a self-serve purchase any member may start.
        productId ||
        (managedOrgId && organizationMembership.canManageChildOrganizations) ||
        organizationMembership.isAdmin ||
        organizationMembership.canManageBilling
    );

const hasActiveLicense = (organizationMembership: OrganizationMemberDetailsModel, now: Date) => {
    const { hasLicense, licenseExpirationDate } = organizationMembership.organization;
    return Boolean(hasLicense && (!licenseExpirationDate || new Date(licenseExpirationDate) >= now));
};

export const getBillingRedirectDecision = ({
    organizationId,
    organizationMembership,
    managedOrgId,
    productId,
    now = new Date(),
}: IGetBillingRedirectDecisionProps): BillingRedirectDecision => {
    if (!hasBillingPermissions({ organizationMembership, managedOrgId, productId })) {
        return { type: 'permissionDenied' };
    }

    // A managed org is always billed through its parent's managed portal
    if (managedOrgId) {
        return {
            type: 'openExternalBilling',
            request: { type: 'managedOrgBillingLink', organizationId, managedOrgId },
            errorRedirectPath: `/org/${organizationId}/managed/${managedOrgId}/billing/redirect/error`,
        };
    }

    const { hasLicense, isReseller, customBillingUrl, trials } = organizationMembership.organization;
    const sortedTrials = sortTrialsByExpiration(trials);
    const activeTrial = sortedTrials.find((trial) => new Date(trial.expirationDate) >= now);
    const errorRedirectPath = hasLicense
        ? `/org/${organizationId}/billing/redirect/error`
        : `/org/${organizationId}/trial/redirect/error`;
    const openExternalBilling = (request: BillingLinkRequest): BillingRedirectDecision => ({
        type: 'openExternalBilling',
        request,
        errorRedirectPath,
    });

    if (productId) {
        // product selection
        if (!isReseller && hasActiveLicense(organizationMembership, now)) {
            return openExternalBilling({ type: 'licensedOrgProductLink', organizationId, productId });
        }
        if (!isReseller && activeTrial) {
            return openExternalBilling({
                type: 'trialOrgProductLink',
                organizationId,
                trialId: activeTrial.id,
                productId,
            });
        }
        return openExternalBilling({ type: 'orgProductLink', organizationId, productId });
    }

    // Existing Subscription
    const hasBillingPortal =
        hasActiveLicense(organizationMembership, now) ||
        Boolean(customBillingUrl) ||
        isStripeTrial(activeTrial ?? sortedTrials[0]);

    if (hasBillingPortal) {
        return openExternalBilling({ type: 'orgBillingLink', organizationId });
    }

    // Explore Products
    return { type: 'navigateToCompare', comparePath: `/org/${organizationId}/billing/compare` };
};

/** The one place a decided request turns into an API call. */
export const resolveBillingLink = (resolvers: IBillingLinkResolvers, request: BillingLinkRequest) => {
    switch (request.type) {
        case 'orgProductLink':
            return resolvers.getOrganizationProductLink(request.organizationId, request.productId);
        case 'licensedOrgProductLink':
            return resolvers.getBillingLinkForOrgByProduct(request.organizationId, request.productId);
        case 'trialOrgProductLink':
            return resolvers.getTrialUpgradeLinkForProduct(request.organizationId, request.trialId, request.productId);
        case 'managedOrgBillingLink':
            return resolvers.getBillingLinkForManagedOrg(request.organizationId, request.managedOrgId);
        case 'orgBillingLink':
            return resolvers.getBillingLink(request.organizationId);
        default:
            return assertUnreachable(request);
    }
};
